import type { UserRole } from '@/types/roles'

type RoleValue = UserRole | string | null | undefined

type RoleInput = RoleValue | RoleValue[] | null | undefined

function toArray(value: RoleInput): RoleValue[] {
  if (Array.isArray(value)) return value
  return value != null ? [value] : []
}

export function normalizeRoles(
  inputRoles: RoleInput,
  fallback?: RoleInput
): UserRole[] {
  const roles = toArray(inputRoles)
  const fallbackRoles = roles.length === 0 ? toArray(fallback ?? null) : []
  const combined = [...roles, ...fallbackRoles]

  const normalized = combined
    .filter((role): role is Exclude<RoleValue, null | undefined> => {
      if (role === null || role === undefined) return false
      if (typeof role === 'string') return role.trim().length > 0
      return true
    })
    .map(role => (typeof role === 'string' ? role.toUpperCase() : role))

  return Array.from(new Set(normalized as UserRole[]))
}

interface RoleAware {
  roles?: RoleInput
  role?: RoleValue
}

export function extractUserRoles<T extends RoleAware>(
  user: T | null | undefined
): UserRole[] {
  if (!user) return []
  return normalizeRoles(user.roles ?? null, user.role)
}

export function userHasRole<T extends RoleAware>(
  user: T | null | undefined,
  role: UserRole
): boolean {
  return extractUserRoles(user).includes(role)
}

export function userHasAnyRole<T extends RoleAware>(
  user: T | null | undefined,
  roles: UserRole[]
): boolean {
  if (!roles.length) return false
  const userRoles = extractUserRoles(user)
  return roles.some(role => userRoles.includes(role))
}

export function getPrimaryRole<T extends RoleAware>(
  user: T | null | undefined
): UserRole | undefined {
  const roles = extractUserRoles(user)
  return roles[0]
}
